import React, { useMemo, useState } from "react";
import { Edit, Plus, Trash } from 'lucide-react';
import { Configuracion, Nivel, Instructor, Classroom, EntityType, ReassignStats } from "../types/index";
import ConfigEditModal from "../components/modals/ConfigEditModal";
import ConfigCreateModal from "../components/modals/ConfigCreateModal";
import { useClientContext } from "../contexts/ClientContext";   
import { toast } from 'react-toastify'; 
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "../main";
import { 
        useCreateInstructorMutation, 
        useUpdateInstructorMutation, 
        useDeleteInstructorMutation,
        useCreateNivelMutation, 
        useUpdateNivelMutation, 
        useDeleteNivelMutation,
        useCreateClassroomMutation,
        useUpdateClassroomMutation,
        useDeleteClassroomMutation,
        useBulkUpdateTariffsMutation,
        useConfigurationsQuery,
        useUpdateConfiguracionMutation,
        useCreateConfiguracionMutation,
        useInstructorUsageQuery,
        useLevelUsageQuery,
        useClassroomUsageQuery,
        useReassignInstructorMutation,
        useReassignLevelMutation,
        useReassignClassroomMutation
        } from "../services/hooks";
import { fetchInstructores, fetchNiveles, fetchClassrooms, type BulkUpdateTariffsResponse } from "../services/api";
import NivelInstructorModal from "../components/configuraciones/NivelInstructorModal";
import ClassroomModal from "../components/configuraciones/ClassroomModal";
import PortalPoliciesModal from "../components/configuraciones/PortalPoliciesModal";
import DeleteEntityModal from "../components/configuraciones/DeleteEntityModal";
import TarifasNombradasSection from "../components/configuraciones/TarifasNombradasSection";

const getFrequencyLabel = (weeklySessions: number) => (
    weeklySessions === -1 ? 'Ilimitada' : `${weeklySessions}x Semana`
);

const Configurations: React.FC = () => {
    // Usar React Query para obtener configuraciones
    const { data: configurations = [] } = useConfigurationsQuery();
    
    const [selectedConfig, setSelectedConfig] = useState<Configuracion | null>(null);
    // Config cuyas políticas del portal se están editando (sección aparte de tarifas)
    const [policiesConfig, setPoliciesConfig] = useState<Configuracion | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false); // Estado para controlar la apertura del modal de niveles
    const [editingNivel, setEditingNivel] = useState<Nivel | undefined>(undefined); // Estado para el nivel que se está editando
    const [editingInstructor, setEditingInstructor] = useState<Instructor | undefined>(undefined);
    const [selectedEntityType, setSelectedEntityType] = useState<'nivel' | 'instructor'>('nivel');

    // Estado para crear configuración
    const [isCreateConfigModalOpen, setIsCreateConfigModalOpen] = useState(false);

    // Estados para classrooms
    const [isClassroomModalOpen, setIsClassroomModalOpen] = useState(false);
    const [editingClassroom, setEditingClassroom] = useState<Classroom | undefined>(undefined);

    // B1 — borrado de instructor / nivel / sala. Un solo estado para los tres: el
    // modal es compartido y el flujo (comprobar uso → reasignar → archivar) es
    // idéntico.
    const [entityToDelete, setEntityToDelete] = useState<
        { entityType: EntityType; id: string; name: string } | null
    >(null);
    // Resultado de la reasignación recién hecha: es lo que hace avanzar el modal
    // al paso de archivado.
    const [completedReassign, setCompletedReassign] = useState<
        { target: string; stats: ReassignStats } | undefined
    >(undefined);
    
    // Estados para el preview de actualización de tarifas
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewData, setPreviewData] = useState<BulkUpdateTariffsResponse | null>(null);
    const [pendingConfig, setPendingConfig] = useState<Configuracion | null>(null);
    const [showAllChanges, setShowAllChanges] = useState(false);

    //estado de los clientes activos
    const { updateActiveClients } = useClientContext();

    // Mutation para actualización masiva de tarifas
    const bulkUpdateMutation = useBulkUpdateTariffsMutation();
    
    // Mutation para actualizar configuraciones
    const updateConfigMutation = useUpdateConfiguracionMutation();

    // Mutation para crear configuraciones
    const createConfigMutation = useCreateConfiguracionMutation();

    const handleEdit = (config: Configuracion) => {
        setSelectedConfig(config);
    };

    // Guardar SOLO las políticas del portal + notificaciones (sin recálculo de tarifas)
    const handleSavePolicies = async (updated: Configuracion) => {
        try {
            await updateConfigMutation.mutateAsync({ id: updated._id, data: updated });
            toast.success('Políticas del portal actualizadas');
        } catch (error) {
            console.error('Error al guardar políticas:', error);
            toast.error('Error guardando las políticas');
        }
    };



    const handleSave = async (updatedConfig: Configuracion) => {
        try {
            // Obtener la configuración actual
            const currentConfig = configurations.find(config => config._id === updatedConfig._id);
            if (!currentConfig) {
                throw new Error('Configuración actual no encontrada');
            }
        
            // ========== NUEVA LÓGICA: Actualización masiva de tarifas ==========
            
            // Verificar si las tarifas han cambiado
            const tarifasChanged = JSON.stringify(currentConfig.tarifas) !== JSON.stringify(updatedConfig.tarifas);
            
            if (tarifasChanged) {
                // NO GUARDAR EN BD TODAVÍA - Primero hacer DryRun
                const dryRunResult = await bulkUpdateMutation.mutateAsync({
                    nuevasTarifas: updatedConfig.tarifas,
                    filtros: { soloActivos: true },
                    dryRun: true
                });
                
                // Mapear la respuesta del backend al formato que espera el frontend
                const mappedResult = {
                    ...dryRunResult,
                    cambios: dryRunResult.cambios?.map((cambio: any) => ({
                        suscripcionId: cambio.id,
                        clienteNombre: cambio.cliente,
                        frecuencia: cambio.frecuencia,
                        tarifaAnterior: cambio.antes,
                        tarifaNueva: cambio.despues
                    })) || [],
                    errores: dryRunResult.errores || []
                };
                
                // Guardar el preview y mostrar modal de confirmación
                setPreviewData(mappedResult);
                setPendingConfig(updatedConfig);
                setShowPreviewModal(true);
                
                // No guardar nada todavía, esperar confirmación del usuario
                return;
            }
            
            // ========== FIN NUEVA LÓGICA ==========
            
            // Si NO hay cambios de tarifas, guardar normalmente con React Query
            await updateConfigMutation.mutateAsync({
                id: updatedConfig._id,
                data: updatedConfig
            });
        } catch (error) {
            console.error('Error al guardar cambios:', error);
            toast.error('Error guardando los cambios');
        }
    };

    // Función para confirmar la actualización masiva de tarifas
    const handleConfirmBulkUpdate = async () => {
        if (!pendingConfig || !previewData) return;
        
        try {
            // PASO 1: Guardar las nuevas tarifas en la configuración usando React Query
            await updateConfigMutation.mutateAsync({
                id: pendingConfig._id,
                data: pendingConfig
            });
            
            // PASO 2: Ejecutar la actualización masiva real de suscripciones
            await bulkUpdateMutation.mutateAsync({
                nuevasTarifas: pendingConfig.tarifas,
                filtros: { soloActivos: true },
                updatedBy: 'admin', // Aquí se podría pasar el usuario actual
                dryRun: false
            });
            
            // Cerrar modal y limpiar estados
            setShowPreviewModal(false);
            setPreviewData(null);
            setPendingConfig(null);
            setShowAllChanges(false);
            
            // Actualizar clientes activos en el contexto
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
            queryClient.invalidateQueries({ queryKey: ['clients'] });
            
            toast.success('Tarifas actualizadas correctamente');
        } catch (error) {
            console.error('Error al actualizar tarifas:', error);
            toast.error('Error al actualizar las tarifas');
        }
    };

    // Función para cancelar la actualización masiva
    const handleCancelBulkUpdate = () => {
        setShowPreviewModal(false);
        setPreviewData(null);
        setPendingConfig(null);
        setShowAllChanges(false);
    };

    // Usar react-query para obtener los niveles
    const { data: niveles = [], isLoading: isLoadingNiveles, isError: isErrorNiveles } = useQuery({
        queryKey: ['niveles'],
        queryFn: fetchNiveles,
    });

     // Usar react-query para obtener los instructores
     const { data: instructores = [], isLoading: isLoadingInstructors, isError: isErrorInstructors } = useQuery({
        queryKey: ['instructores'],
        queryFn: fetchInstructores,
    });

    // Usar react-query para obtener las classrooms
    const { data: classrooms = [] } = useQuery({
        queryKey: ['classrooms'],
        queryFn: fetchClassrooms,
    });

     //llamando a las mutaciones para crear, modificar o eliminar un nivel
     const {mutateAsync: createNivel } = useCreateNivelMutation();
     const {mutateAsync: updateNivel } = useUpdateNivelMutation();
     const deleteNivelMut = useDeleteNivelMutation();

     // Mutaciones para instructores
     const { mutateAsync: createInstructor } = useCreateInstructorMutation();
     const { mutateAsync: updateInstructor } = useUpdateInstructorMutation();
     const deleteInstructorMut = useDeleteInstructorMutation();

     // Mutaciones para classrooms
     const { mutateAsync: createClassroom } = useCreateClassroomMutation();
     const { mutateAsync: updateClassroom } = useUpdateClassroomMutation();
     const deleteClassroomMut = useDeleteClassroomMutation();

     // B1 — reasignación previa al borrado
     const reassignInstructorMut = useReassignInstructorMutation();
     const reassignLevelMut = useReassignLevelMutation();
     const reassignClassroomMut = useReassignClassroomMutation();

     // Uso de la entidad que se está intentando eliminar. Solo se dispara la
     // consulta del tipo que toca; las otras dos quedan deshabilitadas.
     const instructorUsage = useInstructorUsageQuery(
         entityToDelete?.entityType === 'instructor' ? entityToDelete.id : undefined
     );
     const levelUsage = useLevelUsageQuery(
         entityToDelete?.entityType === 'level' ? entityToDelete.id : undefined
     );
     const classroomUsage = useClassroomUsageQuery(
         entityToDelete?.entityType === 'classroom' ? entityToDelete.id : undefined
     );
     const activeUsage = entityToDelete?.entityType === 'instructor'
         ? instructorUsage
         : entityToDelete?.entityType === 'level'
             ? levelUsage
             : classroomUsage;

     // Candidatos a destino: los del mismo tipo, menos el que se va a eliminar.
     const reassignOptions = useMemo(() => {
         if (!entityToDelete) return [];
         const { entityType, id } = entityToDelete;
         if (entityType === 'instructor') {
             return instructores
                 .filter((i: Instructor) => i._id && i._id !== id)
                 .map((i: Instructor) => ({ _id: i._id as string, name: i.nombre }));
         }
         if (entityType === 'level') {
             return niveles
                 .filter((n: Nivel) => n._id && n._id !== id)
                 .map((n: Nivel) => ({ _id: n._id as string, name: n.nombre }));
         }
         return classrooms
             .filter((c: Classroom) => c._id && c._id !== id)
             .map((c: Classroom) => ({ _id: c._id as string, name: c.name }));
     }, [entityToDelete, instructores, niveles, classrooms]);

     const isReassigning = reassignInstructorMut.isPending
         || reassignLevelMut.isPending
         || reassignClassroomMut.isPending;
     const isDeleting = deleteInstructorMut.isPending
         || deleteNivelMut.isPending
         || deleteClassroomMut.isPending;

     const closeDeleteModal = () => {
         setEntityToDelete(null);
         setCompletedReassign(undefined);
     };

     // Mueve el uso futuro al destino elegido. Si sale bien, el modal pasa solo al
     // paso de archivado; si falla, el hook ya ha enseñado el mensaje del servidor
     // (que en un choque explica qué clase estorba y en qué fecha).
     const handleReassign = async (targetId: string) => {
         if (!entityToDelete) return;
         const { entityType, id } = entityToDelete;
         try {
             const response = entityType === 'instructor'
                 ? await reassignInstructorMut.mutateAsync({ instructorId: id, targetId })
                 : entityType === 'level'
                     ? await reassignLevelMut.mutateAsync({ nivelId: id, targetId })
                     : await reassignClassroomMut.mutateAsync({ classroomId: id, targetId });

             setCompletedReassign({ target: response.target, stats: response.stats });
             toast.success('Reasignación completada');
             // La plantilla semanal sí se reasigna, pero los eventos ya generados
             // a partir de ella no vuelven a generarse solos: conviene revisarla.
             toast.info('⚠️ Revisa la PLANTILLA DE EVENTOS si vas a regenerar clases.', {
                 autoClose: false,
                 position: 'top-center',
                 toastId: 'reassign-template-reminder',
                 className: 'border-l-4 border-blue-500',
             });
         } catch {
             // El onError del hook ya ha mostrado el toast.
         }
     };

     // Borra o archiva, según decida el backend. Un 409 aquí significa que el uso
     // cambió entre que se abrió el modal y se pulsó el botón, así que se vuelve a
     // consultar para que el modal enseñe el paso correcto.
     const handleDeleteEntity = async () => {
         if (!entityToDelete) return;
         const { entityType, id } = entityToDelete;
         try {
             if (entityType === 'instructor') await deleteInstructorMut.mutateAsync(id);
             else if (entityType === 'level') await deleteNivelMut.mutateAsync(id);
             else await deleteClassroomMut.mutateAsync(id);
             closeDeleteModal();
         } catch {
             setCompletedReassign(undefined);
             activeUsage.refetch();
         }
     };


    // Función para guardar un nivel (crear o actualizar)
    const handleSaveNivel = (nivel: { nombre: string; color?: string }) => {
        if (editingNivel) {
            updateNivel({
                _id: editingNivel._id,
                nombre: nivel.nombre,
                color: nivel.color,
            });
        } else {
            createNivel({
                nombre: nivel.nombre,
                color: nivel.color,
            });
        }
    };

     // Función para guardar un nivel (crear o actualizar)
     const handleSaveInstructor = (instructor: { nombre: string; email?: string }) => {
        // El correo viaja SIEMPRE, incluso vacío: cadena vacía significa quitarlo,
        // que no es lo mismo que no tocarlo, y el backend distingue los dos casos.
        // Sin mandarlo, poner un correo a un instructor existente no invitaba a
        // nadie porque el servidor nunca llegaba a verlo.
        if (editingInstructor) {
            updateInstructor({
                _id: editingInstructor._id,
                nombre: instructor.nombre,
                email: instructor.email ?? '',
            });
        } else {
            createInstructor({
                nombre: instructor.nombre,
                email: instructor.email ?? '',
            });
        }
    };

     // Manejador genérico para guardar
    const handleSaveNivelInstructor = (entity: { nombre: string; color?: string; email?: string }) => {
        if (selectedEntityType === 'nivel') {
            handleSaveNivel(entity);
        } else {
            handleSaveInstructor(entity);
        }
    };

    // Función para editar un nivel
    const handleEditNivel = (nivel: Nivel) => {
        setEditingNivel(nivel);
        setSelectedEntityType('nivel');
        setIsModalOpen(true);
    };

     // Función para editar un nivel
     const handleEditInstructor = (instructor: Instructor) => {
        setEditingInstructor(instructor);
        setSelectedEntityType('instructor');
        setIsModalOpen(true);
    };


    // B1 — ya no se borra al pulsar: se abre el modal, que consulta el uso y
    // decide entre borrar, archivar o exigir una reasignación previa.
    const handleDeleteNivel = (nivel: Nivel) => {
        setCompletedReassign(undefined);
        setEntityToDelete({ entityType: 'level', id: nivel._id || '', name: nivel.nombre });
    };

    const handleDeleteInstructor = (instructor: Instructor) => {
        setCompletedReassign(undefined);
        setEntityToDelete({ entityType: 'instructor', id: instructor._id || '', name: instructor.nombre });
    };

    // Funciones para classrooms
    const handleSaveClassroom = (data: { name: string; capacity: number }) => {
        if (editingClassroom) {
            updateClassroom({ _id: editingClassroom._id, ...data });
        } else {
            createClassroom(data);
        }
    };

    const handleEditClassroom = (classroom: Classroom) => {
        setEditingClassroom(classroom);
        setIsClassroomModalOpen(true);
    };

    const handleDeleteClassroom = (classroom: Classroom) => {
        setCompletedReassign(undefined);
        setEntityToDelete({ entityType: 'classroom', id: classroom._id || '', name: classroom.name });
    };



    if (!configurations) {
        return <div>Cargando...</div>;
    }

    return (
        <div className="container mx-auto py-20 px-14">
            {/* Header */}
            <div className="overflow-y-auto mb-6">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Configuraciones</h1>
                <p className="text-gray-600">Gestiona las tarifas, instructores y niveles del sistema</p>
            </div>

            {/* Configuraciones de Tarifas */}
            <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Tarifas por Frecuencia</h2>
                    {!configurations.length && (<button
                        onClick={() => setIsCreateConfigModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded"
                    >
                        <Plus size={16} /> Nueva Configuración
                    </button>)}
                </div>
                {configurations.length > 0 ? (
                    <div className="space-y-4">
                        {configurations.map((config) => (
                            <div key={config._id} className="bg-white rounded-lg shadow border border-gray-200 p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold text-gray-800">{config.name}</h3>
                                    <button 
                                        onClick={() => handleEdit(config)} 
                                        className="flex items-center gap-2 px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded"
                                    >
                                        <Edit size={16} /> Modificar
                                    </button>
                                </div>
                                
                                <div>
                                    {/* Tarifas */}
                                    <div>
                                        <h4 className="text-md font-semibold text-gray-700 mb-1">Tarifa general</h4>
                                        <p className="text-xs text-gray-500 mb-3">
                                            {config.tarifasNombradas?.length
                                                ? 'Para los niveles que no están en ninguna tarifa por nivel.'
                                                : 'Se aplica a todos los niveles: se cuentan todas las clases del cliente juntas.'}
                                        </p>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                            {config.tarifas.map((tarifa, idx) => (
                                                <div key={idx} className="border border-gray-300 rounded p-3">
                                                    <div className="text-sm text-gray-600 mb-1">{getFrequencyLabel(tarifa.weeklySessions)}</div>
                                                    <div className="text-lg font-bold text-gray-800">{tarifa.price}€</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {configurations.map((config) => (
                            <TarifasNombradasSection key={`tn-${config._id}`} config={config} />
                        ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-lg p-6 text-center border border-gray-200">
                        <p className="text-gray-500">No se encontraron configuraciones.</p>
                    </div>
                )}
            </div>

            {/* Políticas del portal de clientes */}
            <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Políticas del portal de clientes</h2>
                {configurations.length > 0 ? (
                    <div className="space-y-4">
                        {configurations.map((config) => (
                            <div key={config._id} className="bg-white rounded-lg shadow border border-gray-200 p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold text-gray-800">{config.name}</h3>
                                    <button
                                        onClick={() => setPoliciesConfig(config)}
                                        className="flex items-center gap-2 px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded"
                                    >
                                        <Edit size={16} /> Modificar
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="border border-gray-200 rounded p-3">
                                        <div className="text-xs text-gray-500 mb-1">Horas mín. para recuperación</div>
                                        <div className="text-lg font-bold text-gray-800">{config.minCancellationHours ?? 24} h</div>
                                    </div>
                                    <div className="border border-gray-200 rounded p-3">
                                        <div className="text-xs text-gray-500 mb-1">Antelación máx. con recuperación</div>
                                        <div className="text-lg font-bold text-gray-800">
                                            {config.cancellationHorizonDays != null ? `${config.cancellationHorizonDays} días` : 'Sin límite'}
                                        </div>
                                    </div>
                                    <div className="border border-gray-200 rounded p-3">
                                        <div className="text-xs text-gray-500 mb-1">Plazo para recuperar</div>
                                        {/* La regla base es "dentro del mes"; los días configurados solo
                                            aplican a la última semana, y solo si la excepción está activa. */}
                                        <div className="text-lg font-bold text-gray-800">Dentro del mes</div>
                                        <div className="text-[11px] text-gray-400">
                                            {config.lastWeekCarryover !== false
                                                ? `Última semana: ${config.rescheduleDays ?? 15} días`
                                                : 'Sin excepción de última semana'}
                                        </div>
                                    </div>
                                    <div className="border border-gray-200 rounded p-3">
                                        <div className="text-xs text-gray-500 mb-1">Calendario del cliente</div>
                                        <div className="text-lg font-bold text-gray-800">
                                            {config.visibleMonths ?? 1} {(config.visibleMonths ?? 1) === 1 ? 'mes' : 'meses'}
                                        </div>
                                    </div>
                                    <div className="border border-gray-200 rounded p-3">
                                        <div className="text-xs text-gray-500 mb-1">Notificaciones email</div>
                                        <div className={`text-lg font-bold ${config.notificationsEnabled !== false ? 'text-green-600' : 'text-gray-400'}`}>
                                            {config.notificationsEnabled !== false ? 'Activadas' : 'Desactivadas'}
                                        </div>
                                    </div>
                                    {/* D1 — sale apagada por defecto, así que sin verla aquí no hay
                                        forma de saber si el centro la tiene activa sin abrir el modal. */}
                                    <div className="border border-gray-200 rounded p-3">
                                        <div className="text-xs text-gray-500 mb-1">Lista de espera</div>
                                        <div className={`text-lg font-bold ${(config.waitlistMode ?? 'off') !== 'off' ? 'text-green-600' : 'text-gray-400'}`}>
                                            {config.waitlistMode === 'auto' && 'Automática'}
                                            {config.waitlistMode === 'manual' && 'Por orden de reserva'}
                                            {(config.waitlistMode ?? 'off') === 'off' && 'Desactivada'}
                                        </div>
                                        {(config.waitlistMode ?? 'off') !== 'off' && (
                                            <div className="text-[11px] text-gray-400">
                                                Se cierra {config.waitlistCutoffMinutes ?? 30} min antes de la clase
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-lg p-6 text-center border border-gray-200">
                        <p className="text-gray-500">No se encontraron configuraciones.</p>
                    </div>
                )}
            </div>

            {/* Salas */}
            <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Salas</h2>
                <div className="bg-white rounded-lg shadow border border-gray-200">
                    <div className="p-4 border-b">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-800">Salas disponibles</h3>
                            <button
                                onClick={() => {
                                    setEditingClassroom(undefined);
                                    setIsClassroomModalOpen(true);
                                }}
                                className="flex items-center gap-2 px-3 py-2 bg-brand-primary hover:brightness-90 text-white rounded"
                            >
                                <Plus size={16} /> Crear
                            </button>
                        </div>
                    </div>
                    <div className="p-4">
                        {classrooms.length > 0 ? (
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                {classrooms.map((classroom, index) => (
                                    <div key={classroom._id || index} className="flex justify-between items-center border border-gray-200 p-3 rounded hover:bg-gray-50">
                                        <div>
                                            <span className="font-medium text-gray-800">{classroom.name}</span>
                                            <span className="ml-3 text-sm text-gray-500">
                                                {classroom.capacity} {classroom.capacity === 1 ? 'plaza' : 'plazas'}
                                            </span>
                                            {!classroom._id && (
                                                <span className="ml-2 text-xs text-amber-600">(vuelve a guardarla desde admin para poder editarla)</span>
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleEditClassroom(classroom)}
                                                disabled={!classroom._id}
                                                className="p-2 bg-brand-primary hover:brightness-90 text-white rounded disabled:opacity-40 disabled:cursor-not-allowed"
                                                title="Editar"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClassroom(classroom)}
                                                disabled={!classroom._id}
                                                className="p-2 bg-red-500 hover:bg-red-600 text-white rounded disabled:opacity-40 disabled:cursor-not-allowed"
                                                title="Eliminar"
                                            >
                                                <Trash size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6 text-gray-500">
                                <p>No hay salas registradas</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Instructores y Niveles */}
            <div>
                <h2 className="text-xl font-bold text-gray-800 mb-4">Instructores y Niveles</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Instructores */}
                    <div className="bg-white rounded-lg shadow border border-gray-200">
                        <div className="p-4 border-b">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-semibold text-gray-800">Instructores</h3>
                                <button 
                                    onClick={() => {
                                        setSelectedEntityType('instructor');
                                        setEditingInstructor(undefined);
                                        setIsModalOpen(true);
                                    }} 
                                    className="flex items-center gap-2 px-3 py-2 bg-brand-primary hover:brightness-90 text-white rounded"
                                >
                                    <Plus size={16} /> Crear
                                </button>
                            </div>
                        </div>
                        
                        <div className="p-4">
                            {instructores.length > 0 ? (
                                <div className="space-y-2 max-h-96 overflow-y-auto">
                                    {instructores.map((instructor) => (
                                        <div key={instructor._id} className="flex justify-between items-center border border-gray-200 p-3 rounded hover:bg-gray-50">
                                            <div className="min-w-0">
                                                <span className="font-medium text-gray-800">
                                                    {instructor.nombre}
                                                </span>
                                                {/* P6 fase 2 — de un vistazo, quién puede
                                                    entrar a pasar lista y quién es solo un
                                                    nombre del horario. */}
                                                {instructor.email ? (
                                                    <div className="text-xs text-gray-500 truncate" title={instructor.email}>
                                                        {instructor.email}
                                                        {instructor.clerkUserId
                                                            ? <span className="ml-1 text-green-700">· activo</span>
                                                            : <span className="ml-1 text-amber-700">· invitación pendiente</span>}
                                                    </div>
                                                ) : (
                                                    <div className="text-xs text-gray-400">Sin acceso</div>
                                                )}
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleEditInstructor(instructor)}
                                                    className="p-2 bg-brand-primary hover:brightness-90 text-white rounded"
                                                    title="Editar"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteInstructor(instructor)}
                                                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded"
                                                    title="Eliminar"
                                                >
                                                    <Trash size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-gray-500">
                                    <p>No hay instructores registrados</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Niveles */}
                    <div className="bg-white rounded-lg shadow border border-gray-200">
                        <div className="p-4 border-b">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-semibold text-gray-800">Niveles</h3>
                                <button 
                                    onClick={() => {
                                        setSelectedEntityType('nivel');
                                        setEditingNivel(undefined);
                                        setIsModalOpen(true);
                                    }} 
                                    className="flex items-center gap-2 px-3 py-2 bg-brand-primary hover:brightness-90 text-white rounded"
                                >
                                    <Plus size={16} /> Crear
                                </button>
                            </div>
                        </div>
                        
                        <div className="p-4">
                            {niveles.length > 0 ? (
                                <div className="space-y-2 max-h-96 overflow-y-auto">
                                    {niveles.map((nivel) => (
                                        <div key={nivel._id} className="flex justify-between items-center border border-gray-200 p-3 rounded hover:bg-gray-50">
                                            <div className="flex items-center gap-3">
                                                <div 
                                                    className="w-8 h-8 rounded border-2 border-gray-300 shadow-sm flex-shrink-0"
                                                    style={{ backgroundColor: nivel.color || '#40a136' }}
                                                    title={`Color: ${nivel.color || '#40a136'}`}
                                                />
                                                <span className="font-medium text-gray-800">
                                                    {nivel.nombre}
                                                </span>
                                            </div>
                                            <div className="flex gap-2">
                                                <button 
                                                    onClick={() => handleEditNivel(nivel)} 
                                                    className="p-2 bg-brand-primary hover:brightness-90 text-white rounded"
                                                    title="Editar"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteNivel(nivel)} 
                                                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded"
                                                    title="Eliminar"
                                                >
                                                    <Trash size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-gray-500">
                                    <p>No hay niveles registrados</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Modales */}
            {isCreateConfigModalOpen && (
                    <ConfigCreateModal
                        onClose={() => setIsCreateConfigModalOpen(false)}
                        onSave={(newConfig) => createConfigMutation.mutate(newConfig)}
                    />
                )}
            {selectedConfig && (
                    <ConfigEditModal
                        config={selectedConfig}
                        onClose={() => setSelectedConfig(null)}
                        onSave={handleSave}
                    />
                )}
            {policiesConfig && (
                    <PortalPoliciesModal
                        config={policiesConfig}
                        onClose={() => setPoliciesConfig(null)}
                        onSave={handleSavePolicies}
                    />
                )}
                <NivelInstructorModal 
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        setEditingNivel(undefined);
                        setEditingInstructor(undefined);
                    }}
                    onSave={handleSaveNivelInstructor}
                    entity={selectedEntityType === 'nivel' ? editingNivel : editingInstructor}
                    entityType={selectedEntityType}
                    usedColors={niveles.map((n) => n.color)}
                />
                <ClassroomModal
                    isOpen={isClassroomModalOpen}
                    onClose={() => {
                        setIsClassroomModalOpen(false);
                        setEditingClassroom(undefined);
                    }}
                    onSave={handleSaveClassroom}
                    classroom={editingClassroom}
                />

                {/* B1 — borrado de instructor / nivel / sala con reasignación previa */}
                {entityToDelete && (
                    <DeleteEntityModal
                        isOpen
                        entityType={entityToDelete.entityType}
                        name={entityToDelete.name}
                        options={reassignOptions}
                        usage={activeUsage.data}
                        isLoadingUsage={activeUsage.isLoading}
                        isReassigning={isReassigning}
                        isDeleting={isDeleting}
                        completedReassign={completedReassign}
                        onReassign={handleReassign}
                        onDelete={handleDeleteEntity}
                        onClose={closeDeleteModal}
                    />
                )}

                {/* Modal de Preview para Actualización Masiva de Tarifas */}
                {showPreviewModal && previewData && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                            <div className="p-6 border-b">
                                <h2 className="text-2xl font-bold text-gray-800">
                                    Confirmar Actualización de Tarifas
                                </h2>
                                <p className="text-gray-600 mt-2">
                                    Se actualizarán <strong>{previewData.actualizadas}</strong> suscripciones activas
                                </p>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto p-6">
                                {/* Resumen Estadístico */}
                                {previewData.detalle && (
                                    <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5">
                                        <h3 className="font-bold text-blue-900 mb-4 text-lg flex items-center gap-2">
                                            📊 Resumen Estadístico
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Por Tipo */}
                                            {previewData.detalle.porTipo && (
                                                <div className="bg-white rounded-lg p-4 shadow-sm">
                                                    <h4 className="font-semibold text-gray-700 mb-2 text-sm">Por Tipo de Suscripción</h4>
                                                    <div className="space-y-1">
                                                        {Object.entries(previewData.detalle.porTipo).map(([tipo, cantidad]) => (
                                                            <div key={tipo} className="flex justify-between items-center">
                                                                <span className="text-gray-600 text-sm">{tipo}</span>
                                                                <span className="font-bold text-blue-600">{cantidad}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Por Frecuencia */}
                                            {previewData.detalle.porFrecuencia && (
                                                <div className="bg-white rounded-lg p-4 shadow-sm">
                                                    <h4 className="font-semibold text-gray-700 mb-2 text-sm">Por Frecuencia</h4>
                                                    <div className="space-y-1">
                                                        {Object.entries(previewData.detalle.porFrecuencia).map(([frecuencia, cantidad]) => (
                                                            <div key={frecuencia} className="flex justify-between items-center">
                                                                <span className="text-gray-600 text-sm">{frecuencia}</span>
                                                                <span className="font-bold text-blue-600">{cantidad}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {previewData.cambios && previewData.cambios.length > 0 && (
                                    <div className="space-y-4">
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <div className="flex justify-between items-center mb-3">
                                                <h3 className="font-semibold text-blue-900">
                                                    📋 Detalles de los Cambios
                                                </h3>
                                                <span className="text-sm text-gray-600">
                                                    {showAllChanges 
                                                        ? `Mostrando todos (${previewData.cambios.length})`
                                                        : `Mostrando ${Math.min(20, previewData.cambios.length)} de ${previewData.cambios.length}`
                                                    }
                                                </span>
                                            </div>
                                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                                {(showAllChanges ? previewData.cambios : previewData.cambios.slice(0, 20)).map((cambio, index) => (
                                                    <div 
                                                        key={index} 
                                                        className="bg-white p-3 rounded border border-blue-100"
                                                    >
                                                        <div className="flex justify-between items-center">
                                                            <span className="font-medium text-gray-800">
                                                                {cambio.clienteNombre || 'Cliente sin nombre'}
                                                            </span>
                                                            <span className="text-sm text-gray-600">
                                                                {cambio.frecuencia || 'N/A'}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1 text-sm">
                                                            <span className="text-red-600 line-through">
                                                                {typeof cambio.tarifaAnterior === 'number' ? cambio.tarifaAnterior.toFixed(2) : '0.00'}€
                                                            </span>
                                                            <span className="text-gray-400">→</span>
                                                            <span className="text-green-600 font-semibold">
                                                                {typeof cambio.tarifaNueva === 'number' ? cambio.tarifaNueva.toFixed(2) : '0.00'}€
                                                            </span>
                                                            <span className={`ml-auto font-medium ${
                                                                (cambio.tarifaNueva || 0) > (cambio.tarifaAnterior || 0)
                                                                    ? 'text-green-600' 
                                                                    : 'text-red-600'
                                                            }`}>
                                                                {(cambio.tarifaNueva || 0) > (cambio.tarifaAnterior || 0) ? '+' : ''}
                                                                {typeof cambio.tarifaNueva === 'number' && typeof cambio.tarifaAnterior === 'number' 
                                                                    ? (cambio.tarifaNueva - cambio.tarifaAnterior).toFixed(2)
                                                                    : '0.00'
                                                                }€
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            
                                            {/* Botón Ver Todos */}
                                            {!showAllChanges && previewData.cambios.length > 20 && (
                                                <button
                                                    onClick={() => setShowAllChanges(true)}
                                                    className="w-full mt-3 py-2 text-blue-600 hover:text-blue-800 font-medium text-sm border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                                                >
                                                    Ver todos los {previewData.cambios.length} cambios ↓
                                                </button>
                                            )}
                                            {showAllChanges && (
                                                <button
                                                    onClick={() => setShowAllChanges(false)}
                                                    className="w-full mt-3 py-2 text-blue-600 hover:text-blue-800 font-medium text-sm border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                                                >
                                                    Mostrar solo primeros 20 ↑
                                                </button>
                                            )}
                                        </div>
                                        
                                        {previewData.errores && previewData.errores.length > 0 && (
                                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                                <h3 className="font-semibold text-red-900 mb-3">
                                                    ⚠️ Errores Detectados ({previewData.errores.length})
                                                </h3>
                                                <div className="space-y-2 max-h-96 overflow-y-auto">
                                                    {previewData.errores.map((error, index) => (
                                                        <div 
                                                            key={index} 
                                                            className="bg-white p-3 rounded border border-red-100 text-sm text-red-700"
                                                        >
                                                            <div><strong>ID:</strong> {error.suscripcionId || 'N/A'}</div>
                                                            <div><strong>Error:</strong> {error.error || 'Error desconocido'}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            <div className="p-6 border-t bg-gray-50">
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={handleCancelBulkUpdate}
                                        className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                                        disabled={bulkUpdateMutation.isPending}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleConfirmBulkUpdate}
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                                        disabled={bulkUpdateMutation.isPending || (previewData.errores && previewData.errores.length > 0)}
                                    >
                                        {bulkUpdateMutation.isPending ? 'Actualizando...' : 'Confirmar y Actualizar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
};

export default Configurations;